import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { AllowedTimes } from 'src/features/membershipType/types';
import AllowedTimeForm from 'src/features/allowedTimes/components/forms/AllowedTimeForm';
import {
    AllowedTimeFormSchemaValues,
    customAllowedTimeFormSchema,
    CustomAllowedTimeFormSchemaValues,
} from 'src/features/allowedTimes/utils/formSchema';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { queryClient } from 'src/lib/react-query';
import { LIST_ALLOWED_TIMES_RESOURCE } from 'src/features/allowedTimes/api/membershipType/useListMembershipTypeAllowedTimes';
import { MEMBERSHIP_TYPE_RESOURCE, useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { toast } from 'src/hooks/UseToast';
import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';
import { LIST_MEMBERSHIP_ALLOWED_TIMES_RESOURCE } from 'src/features/allowedTimes/api/membership/useListMembershipAllowedTimes';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { useParams } from 'react-router-dom';
import { useParking } from 'src/providers/ParkingProvider';

interface AllowedTimeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowedTime?: AllowedTimes;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: AllowedTimeFormSchemaValues[],
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function AllowedTimeFormModal<TData>({
    selectedAllowedTime,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: AllowedTimeFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { membershipTypeId = '', membershipId = '' } = useParams();
    const { parkingId } = useParking();

    const onError = useOnError();

    const { data: membershipTypeData } = useGetMembershipType({
        membershipTypeId: membershipTypeId,
        parkingId,
        config: { enabled: !!membershipTypeId && isModalOpen, staleTime: 0, refetchOnMount: 'always' },
    });
    const isActiveTemplate = Boolean(membershipTypeData?.is_active);

    const defaultValues = React.useMemo(
        () =>
            selectedAllowedTime
                ? {
                      ...selectedAllowedTime,
                      day_of_week: [selectedAllowedTime?.day_of_week],
                  }
                : undefined,
        [selectedAllowedTime],
    );
    const form = useForm<CustomAllowedTimeFormSchemaValues>({
        resolver: zodResolver(customAllowedTimeFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [defaultValues, isModalOpen, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: CustomAllowedTimeFormSchemaValues) => {
            const allowedTimeShapeData = values.day_of_week.map((week) => ({
                day_of_week: week,
                from_time: values.from_time,
                to_time: values.to_time,
            }));

            mutate(
                allowedTimeShapeData,
                () => {
                    setIsModalOpen(false);
                    if (selectedAllowedTime) return;
                    void queryClient.invalidateQueries({ queryKey: [LIST_ALLOWED_TIMES_RESOURCE] });
                    void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
                    void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_ALLOWED_TIMES_RESOURCE] });
                    toast({
                        title: formatMessage(allowedTimesMessages.createAllowedTimeSuccess),
                        description: formatMessage(allowedTimesMessages.createAllowedTimeSuccessDescription),
                    });
                },
                (error) => onError(error, form.setError),
            );
        },

        [mutate, formatMessage, onError, form.setError, selectedAllowedTime],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-block">
                                <DialogTrigger asChild>
                                    <Button className={className} disabled={isActiveTemplate && !membershipId}>
                                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                                        {title}
                                    </Button>
                                </DialogTrigger>
                            </span>
                        </TooltipTrigger>
                        {isActiveTemplate && !membershipId && (
                            <TooltipContent side="top" className="max-w-56">
                                <FormattedMessage {...membershipTypeMessages.activeMembershipTypeProtectedError} />
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AllowedTimeForm form={form} onSubmit={onSubmit} isEdit={!!selectedAllowedTime} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedAllowedTime && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AllowedTimeFormModal;
